/**
 * useSessionGreeting — the front-door boot: EVERY session opens with the
 * assistant speaking first.
 *
 * Owns the whole "session greeting" lifecycle for App.tsx:
 *
 *   • On sign-in, gathers REAL context off the render path (standup counts,
 *     the activity-feed diff since lastSeenFeedAt, the live nudge sweep,
 *     workspace emptiness) and composes the greeting via the TOTAL
 *     composeSessionGreeting — then calls `onOpen` so the host auto-opens
 *     the dock. Silence is a bug: even if gathering throws, a zero-context
 *     greeting is composed and shown.
 *
 *   • Once per PAGE LOAD, by ref (a session = a fresh page load/sign-in).
 *     Navigation never re-pops it, and a manual close stays closed — the
 *     dock reducer's 'auto-standup' guard is the second lock.
 *
 *   • The once-per-day lastStandupShownAt stamp only DEMOTES variant (a)
 *     (full standup) to a lighter greeting on later sessions — it never
 *     suppresses the greeting itself.
 *
 *   • settle() clears the card and writes the stamps: lastSeenFeedAt always
 *     (the next session diffs from here), lastStandupShownAt only when a
 *     standup-bearing variant (a/d) was shown — preserving the existing
 *     once-per-day contract.
 */

import { useEffect, useRef, useState } from 'react';
import { shouldShowStandup, composeStandup } from './standup';
import {
  gatherSessionContext,
  markFeedSeen,
  markStandupShown,
  readLastSeenFeedAt,
  readLastStandupShownAt,
} from './standupBoot';
import { listFeed } from './activityFeed';
import {
  composeSessionGreeting,
  feedEntriesSince,
  type SessionGreeting,
} from './sessionGreeting';

export interface UseSessionGreetingOptions {
  /** True once auth resolves to a signed-in user. */
  signedIn: boolean;
  /** Display name for the salutation. */
  userName?: string;
  /** Called exactly when the greeting is ready — the host opens the dock. */
  onOpen: () => void;
}

export interface SessionGreetingHandle {
  /** The composed greeting, or null before boot / after settle. */
  greeting: SessionGreeting | null;
  /** Clear the card + write the stamps (feed-seen always, standup if shown). */
  settle: () => void;
}

/** Compose the greeting from real context; NEVER throw, NEVER null. */
async function bootGreeting(
  now: number,
  userName?: string,
): Promise<SessionGreeting> {
  try {
    const [lastStandupAt, lastSeenFeedAt, context, feed] = await Promise.all([
      readLastStandupShownAt(),
      readLastSeenFeedAt(),
      gatherSessionContext(now),
      listFeed(),
    ]);
    return composeSessionGreeting(now, {
      firstSessionOfDay: shouldShowStandup(lastStandupAt, now),
      standup: composeStandup(now, { ...context.inputs, userName }),
      feedSinceLastSeen: feedEntriesSince(feed, lastSeenFeedAt),
      topNudges: context.topNudges,
      userName,
      workspaceEmpty: context.workspaceEmpty,
    });
  } catch {
    // Gathering failed — the greeting still speaks (zero context composes
    // to the alive all-clear offer). A broken store must not mean silence.
    return composeSessionGreeting(now, {
      firstSessionOfDay: false,
      standup: null,
      feedSinceLastSeen: [],
      topNudges: [],
      userName,
      workspaceEmpty: false,
    });
  }
}

export function useSessionGreeting({
  signedIn,
  userName,
  onOpen,
}: UseSessionGreetingOptions): SessionGreetingHandle {
  const [greeting, setGreeting] = useState<SessionGreeting | null>(null);
  // Mirror for settle(): side effects must not live inside a state updater
  // (StrictMode may invoke updaters twice).
  const greetingRef = useRef<SessionGreeting | null>(null);
  greetingRef.current = greeting;
  // Once per page load: navigation, re-renders, even sign-out → sign-in in
  // the same tab never re-pop the greeting. A session is a fresh load.
  const bootedRef = useRef(false);
  // Keep the latest callbacks/props without retriggering the boot effect.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const userNameRef = useRef(userName);
  userNameRef.current = userName;

  useEffect(() => {
    if (!signedIn || bootedRef.current) return;
    bootedRef.current = true;
    let cancelled = false;
    void (async () => {
      const composed = await bootGreeting(Date.now(), userNameRef.current);
      if (cancelled) return;
      setGreeting(composed);
      onOpenRef.current();
      // HYDRATION RACE GUARD: at sign-in instant the store may not have
      // synced/seeded yet, so a POPULATED workspace can read as empty for a
      // moment — and the operator gets told "your workspace is empty" while
      // staring at his data (this happened; it reads as gaslighting). If the
      // first pass composed the cold-start ask, re-gather once after the app
      // settles and, if data showed up, replace the card with the truthful
      // variant. A genuinely empty workspace just re-confirms the ask.
      if (composed.standup?.coldStart) {
        await new Promise((res) => setTimeout(res, 2500));
        if (cancelled || greetingRef.current !== composed) return;
        const recheck = await bootGreeting(Date.now(), userNameRef.current);
        if (cancelled || greetingRef.current !== composed) return;
        if (!recheck.standup?.coldStart) setGreeting(recheck);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  const settle = () => {
    const g = greetingRef.current;
    if (!g) return;
    greetingRef.current = null;
    setGreeting(null);
    // Baseline for the next session's "while you were away" digest.
    void markFeedSeen().catch(() => undefined);
    // Only a standup-bearing card consumes the once-per-day stamp — lighter
    // variants never demote tomorrow's full standup.
    if (g.standup) void markStandupShown().catch(() => undefined);
  };

  return { greeting, settle };
}
